
import {
  defineComponent,
  computed,
  nextTick,
  onMounted,
  ref,
  watch,
} from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import { useCreatingForm } from "@/core/hooks/use-creating-form";
import { useRouterCustom } from "@/core/hooks/use-router-custom";
import {
  setBreadcrumbLinks,
  setCurrentPageTitle,
} from "@/core/helpers/breadcrumb";
import {
  swalErrNotification,
  swalNotification,
  lengthValidationRules,
} from "@/core/helpers/utils";
import { useButtonCustom } from "@/core/hooks/use-button-custom";
import useFormInput from "@/core/hooks/use-form-input";
import useCapDonVi from "@/core/hooks/use-cap-don-vi";
import { useLoading } from "@/core/hooks/use-loading";
import {
  IApiRoleItem,
  IAuthorizationPermissionGroup,
  IDonViResponseBase,
  PartBaseResponse,
  PositionBaseResponse,
} from "@/core/interfaces/ApiResponses";
import {
  extractPaginatedList,
  filterAssignableRoles,
  groupPermissionsByPrefix,
  hasAdminRole,
  LOCKED_ROLES,
  snapshotAuth,
} from "@/core/helpers/authorization-utils";
import { UserService } from "@/core/services/UserService";
import { DonViService } from "@/core/services/DonVi.service";
import BoPhanService from "@/core/services/BoPhan.service";
import ChucVuService from "@/core/services/ChucVu.service";
import RoleService from "@/core/services/Roles.service";
import PermissionService from "@/core/services/PermissionService";
import { Actions } from "@/store/enums/StoreEnums";
import PageContentBase from "@/layout/_shared/PageContentBase.vue";
import ButtonCustom from "@/components/shared/buttons/ButtonCustom.vue";
import DivRow from "@/layout/_shared/DivRow.vue";
import DivCol from "@/layout/_shared/DivCol.vue";
import LabelCustom from "@/components/shared/label-custom/LabelCustom.vue";
import UserAuthorizationPanel from "@/views/crafted/pages/nguoi-dung/UserAuthorizationPanel.vue";

const emptyForm = (defaultDonViId = 0) => ({
  ho_va_ten: "",
  so_dien_thoai: "",
  mat_khau: "",
  mat_khau_confirm: "",
  id_bo_phan: undefined as number | undefined,
  id_chuc_vu: undefined as number | undefined,
  id_don_vi: defaultDonViId || (undefined as number | undefined),
  email: "",
});

export default defineComponent({
  name: "nguoi-dung-form",

  components: {
    ButtonCustom,
    DivCol,
    DivRow,
    LabelCustom,
    PageContentBase,
    UserAuthorizationPanel,
  },

  setup() {
    const route = useRoute();
    const { isCreating, id, formRef } = useCreatingForm();
    const { goBack, replace } = useRouterCustom();
    const { ButtonTypeColors, ButtonsType } = useButtonCustom();
    const { focusFirstErrInputV2 } = useFormInput();
    const { isUnitLevel, isCompanyLevel } = useCapDonVi();
    const { isLoading, startLoading, endLoading } = useLoading();
    const store = useStore();

    const currentUserDepartmentId = store.getters.currentUser
      ?.departmentId as number;
    const currentUserId = store.getters.currentUser?.id as number;
    const actorRoleNames = computed<string[]>(
      () =>
        store.getters.currentUser?.roles?.map(
          (role: { name: string }) => role.name
        ) || []
    );
    const actorIsAdmin = computed(() => hasAdminRole(actorRoleNames.value));

    // Only admin/superadmin at company level may assign roles/permissions.
    const actorCanAssign = ref(false);

    const loadActorAbilities = async () => {
      await store.dispatch(Actions.LOAD_ABILITIES);
      actorCanAssign.value = actorIsAdmin.value && isCompanyLevel.value;
    };

    const pageLoading = ref(false);
    const form = ref(emptyForm(currentUserDepartmentId));
    const boPhanOptions = ref<PartBaseResponse[]>([]);
    const chucVuOptions = ref<PositionBaseResponse[]>([]);
    const donViOptions = ref<IDonViResponseBase[]>([]);

    const roleOptions = ref<IApiRoleItem[]>([]);
    const permissionGroups = ref<IAuthorizationPermissionGroup[]>([]);
    const selectedRoles = ref<string[]>([]);
    const selectedPermissions = ref<string[]>([]);
    const canManage = ref(false);
    const savingAuth = ref(false);
    const initialAuthSnapshot = ref(snapshotAuth([], []));
    const initialDirectPermissions = ref<string[]>([]);
    const initialEffectivePermissions = ref<string[]>([]);

    const validatePassword = (
      _rule: unknown,
      value: string,
      callback: (error?: Error) => void
    ) => {
      if (isCreating.value) {
        callback();
        return;
      }

      if (!value && !form.value.mat_khau_confirm) {
        callback();
        return;
      }

      if (!value) {
        callback(new Error("Mật khẩu không được để trống"));
        return;
      }

      if (value.length < 8) {
        callback(new Error("Mật khẩu phải có ít nhất 8 ký tự"));
        return;
      }

      if (value.length > 32) {
        callback(new Error("Mật khẩu phải có độ dài từ 8 đến 32 ký tự"));
        return;
      }

      callback();
    };

    const validateConfirmPassword = (
      _rule: unknown,
      value: string,
      callback: (error?: Error) => void
    ) => {
      if (!isCreating.value && !value && !form.value.mat_khau) {
        callback();
        return;
      }

      if (!value) {
        callback(new Error("Mật khẩu xác nhận không được để trống"));
        return;
      }

      if (value.length < 8) {
        callback(new Error("Mật khẩu phải có ít nhất 8 ký tự"));
        return;
      }

      if (value.length > 32) {
        callback(new Error("Mật khẩu phải có độ dài từ 8 đến 32 ký tự"));
        return;
      }

      if (value !== form.value.mat_khau) {
        callback(new Error("Mật khẩu xác nhận không trùng khớp"));
        return;
      }

      callback();
    };

    const validateEmail = (
      _rule: unknown,
      value: string,
      callback: (error?: Error) => void
    ) => {
      if (!value) {
        callback();
        return;
      }

      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(value)) {
        callback(new Error("Email không đúng định dạng"));
        return;
      }

      callback();
    };

    const rules = computed(() => ({
      ho_va_ten: lengthValidationRules({
        name: "Họ và tên",
        min: 1,
        max: 30,
      }),
      so_dien_thoai: lengthValidationRules({
        name: "Số điện thoại",
        min: 1,
        max: 15,
      }),
      ...(isCreating.value
        ? {
            mat_khau: lengthValidationRules({
              name: "Mật khẩu",
              min: 8,
              max: 32,
            }),
          }
        : {
            mat_khau: [
              {
                validator: validatePassword,
                trigger: "blur",
              },
            ],
          }),
      mat_khau_confirm: [
        {
          validator: validateConfirmPassword,
          trigger: "blur",
        },
      ],
      id_bo_phan: [
        {
          required: true,
          message: "Bộ phận không được để trống",
          trigger: "change",
        },
      ],
      id_chuc_vu: [
        {
          required: true,
          message: "Chức vụ không được để trống",
          trigger: "change",
        },
      ],
      id_don_vi: [
        {
          required: true,
          message: "Đơn vị không được để trống",
          trigger: "change",
        },
      ],
      email: [
        {
          validator: validateEmail,
          trigger: "blur",
        },
      ],
    }));

    const ensureDonViId = () => {
      if (isUnitLevel.value) {
        form.value.id_don_vi = currentUserDepartmentId;
      }
    };

    const donViOptionLabel = (item: IDonViResponseBase) => {
      const capLabel = item.cap_don_vi === 1 ? "Cty" : "ĐV";
      return item.ma_don_vi
        ? `[${capLabel}] ${item.ma_don_vi} - ${item.ten_don_vi}`
        : `[${capLabel}] ${item.ten_don_vi}`;
    };

    const appendDonViOptionIfMissing = (donVi: {
      id: number;
      ten_don_vi: string;
      ma_don_vi?: string;
      cap_don_vi?: number;
    }) => {
      if (donViOptions.value.some((item) => item.id === donVi.id)) {
        return;
      }

      donViOptions.value = [
        ...donViOptions.value,
        {
          id: donVi.id,
          ten_don_vi: donVi.ten_don_vi,
          ma_don_vi: donVi.ma_don_vi || "",
          cap_don_vi: donVi.cap_don_vi,
          dia_chi: "",
          parent_id: 0,
          system_id: 0,
          created_at: "",
          updated_at: "",
        } as IDonViResponseBase,
      ];
    };

    const loadDonViOptions = async () => {
      if (isUnitLevel.value) {
        const {
          data: { data },
        } = await DonViService.fetchCurrentDepartmentById(
          currentUserDepartmentId
        );
        donViOptions.value = data ? [data] : [];
        ensureDonViId();
        return;
      }

      const [cap1Res, cap2Res] = await Promise.all([
        DonViService.fetchDepartmentsByCapDonVi(1),
        DonViService.fetchDepartmentsByCapDonVi(2),
      ]);

      donViOptions.value = [
        ...(cap1Res.data.data || []),
        ...(cap2Res.data.data || []),
      ].sort((a, b) => {
        const capDiff = (a.cap_don_vi ?? 0) - (b.cap_don_vi ?? 0);
        if (capDiff !== 0) return capDiff;
        return a.ten_don_vi.localeCompare(b.ten_don_vi);
      });
    };

    // Catalog selects: GET /chuc-vu|bo-phan/danh-sach (full array, no paginate).
    // Roles/permissions still admin-only.
    const loadDropdownData = async () => {
      try {
        const [boPhanList, chucVuList] = await Promise.all([
          BoPhanService.listForSelect(),
          ChucVuService.listForSelect(),
        ]);
        boPhanOptions.value = boPhanList;
        chucVuOptions.value = chucVuList;

        if (actorIsAdmin.value) {
          const rolesRes = await RoleService.list();
          roleOptions.value = filterAssignableRoles(
            extractPaginatedList<IApiRoleItem>(rolesRes.data),
            actorRoleNames.value
          );

          const allPermissions = await PermissionService.listAll();
          permissionGroups.value = groupPermissionsByPrefix(allPermissions);
        }
      } catch (error) {
        await swalErrNotification(
          error,
          "Có lỗi xảy ra khi tải dữ liệu biểu mẫu"
        );
      }
    };

    const resetAuthSelection = () => {
      selectedRoles.value = [];
      selectedPermissions.value = [];
      canManage.value = actorCanAssign.value;
      initialAuthSnapshot.value = snapshotAuth([], []);
      initialDirectPermissions.value = [];
      initialEffectivePermissions.value = [];
    };

    // Convert checkbox selection (user's owned permissions, incl. role-inherited)
    // back to direct grants only: keep originally-direct ones still selected,
    // plus newly checked ones that were not owned before.
    const buildDirectPermissionsPayload = (): string[] => {
      const initialDirect = new Set(initialDirectPermissions.value);
      const initialEffective = new Set(initialEffectivePermissions.value);

      return selectedPermissions.value.filter(
        (name) => initialDirect.has(name) || !initialEffective.has(name)
      );
    };

    const loadUserDetail = async () => {
      try {
        const {
          data: { data },
        } = await UserService.get(id.value);
        const { user, authorization } = data;

        form.value = {
          ho_va_ten: user.ho_va_ten,
          so_dien_thoai: user.so_dien_thoai,
          mat_khau: "",
          mat_khau_confirm: "",
          id_bo_phan: user.id_bo_phan,
          id_chuc_vu: user.id_chuc_vu,
          id_don_vi: isUnitLevel.value
            ? currentUserDepartmentId
            : user.id_don_vi,
          email: user.email || "",
        };

        if (user.don_vi) {
          appendDonViOptionIfMissing({
            id: user.don_vi.id,
            ten_don_vi: user.don_vi.ten_don_vi,
            ma_don_vi: user.don_vi.ma_don_vi,
          });
        }

        // can_manage decides whether the actor may edit this user's
        // roles/permissions. Falsy assigned arrays => empty (no checks).
        canManage.value = authorization.can_manage;
        // Checked = roles/permissions the user currently owns
        // (effective permissions include role-inherited grants).
        const assignedRoleNames = authorization.assigned_role_names || [];
        const effectivePermissions = authorization.effective_permissions || [];
        const directPermissions = authorization.direct_permissions || [];

        selectedRoles.value = assignedRoleNames.filter(
          (name) => !LOCKED_ROLES.includes(name)
        );
        selectedPermissions.value = [...effectivePermissions];
        initialDirectPermissions.value = [...directPermissions];
        initialEffectivePermissions.value = [...effectivePermissions];
        initialAuthSnapshot.value = snapshotAuth(
          selectedRoles.value,
          selectedPermissions.value
        );
      } catch (error) {
        await swalErrNotification(
          error,
          "Có lỗi xảy ra khi lấy thông tin người dùng"
        );
      }
    };

    const syncFormModeFromRoute = () => {
      const routeName = route.name?.toString() || "";
      const isEditRoute =
        routeName.includes("cap-nhat") || routeName.includes("chinh-sua");

      isCreating.value = !isEditRoute;

      if (isEditRoute) {
        id.value = +route.params.id;
      }
    };

    const updatePageMeta = () => {
      setBreadcrumbLinks([
        {
          text: "Người dùng",
          routeData: { name: "danh-sach-nguoi-dung" },
        },
        {
          text: isCreating.value ? "Tạo mới" : "Chỉnh sửa",
        },
      ]);

      setCurrentPageTitle(
        isCreating.value ? "Tạo mới người dùng" : "Chỉnh sửa người dùng"
      );
      document.title = `${
        isCreating.value ? "Tạo mới" : "Chỉnh sửa"
      } - Người dùng - Đường sắt Hà Nội`;
    };

    const loadFormContent = async () => {
      if (!isCreating.value) {
        await loadUserDetail();
        return;
      }

      form.value = emptyForm(currentUserDepartmentId);
      resetAuthSelection();
      ensureDonViId();
      await nextTick();
      formRef.value?.clearValidate();
    };

    const initializePage = async ({
      loadDropdown = false,
    }: { loadDropdown?: boolean } = {}) => {
      syncFormModeFromRoute();
      updatePageMeta();

      pageLoading.value = true;
      await startLoading("Đang tải dữ liệu");

      try {
        // Sequential load order:
        // me/abilities -> don-vi -> chi-tiet -> bo-phan -> chuc-vu -> roles -> permissions
        await loadActorAbilities();
        await loadDonViOptions();
        await loadFormContent();

        if (loadDropdown) {
          await loadDropdownData();
        }
      } finally {
        pageLoading.value = false;
        await endLoading();
      }
    };

    let isInitialMount = true;

    onMounted(async () => {
      await initializePage({ loadDropdown: true });
      isInitialMount = false;
    });

    watch(
      () => route.fullPath,
      async () => {
        if (isInitialMount) return;

        await initializePage();
      }
    );

    const clear = async () => {
      if (isCreating.value) {
        form.value = emptyForm(currentUserDepartmentId);
        resetAuthSelection();
        await nextTick();
        formRef.value?.clearValidate();
        return;
      }

      await startLoading("Đang tải dữ liệu");
      try {
        await loadUserDetail();
      } finally {
        await endLoading();
      }

      await nextTick();
      formRef.value?.clearValidate();
    };

    const syncAbilitiesIfSelf = async (targetUserId: number) => {
      if (targetUserId === currentUserId) {
        await store.dispatch(Actions.LOAD_ABILITIES, true);
      }
    };

    // Toggling any role/permission checkbox immediately syncs via phan-quyen
    // (edit mode only). Create mode keeps selection local until user is created.
    const handleAuthChange = async () => {
      if (isCreating.value || !canManage.value || savingAuth.value) {
        return;
      }

      savingAuth.value = true;
      try {
        await UserService.phanQuyen(id.value, {
          roles: selectedRoles.value,
          permissions: buildDirectPermissionsPayload(),
        });
        await syncAbilitiesIfSelf(id.value);
        // Re-sync checkbox state (role changes cascade to effective perms).
        await loadUserDetail();
      } catch (error) {
        await swalErrNotification(
          error,
          "Không thể cập nhật phân quyền, vui lòng thử lại"
        );
        // Revert to server state on failure.
        await loadUserDetail();
      } finally {
        savingAuth.value = false;
      }
    };

    const submit = () => {
      if (!formRef.value) return;

      formRef.value.validate(async (valid) => {
        if (!valid) {
          await focusFirstErrInputV2();
          return;
        }

        await startLoading("Đang lưu dữ liệu");

        try {
          const { id_bo_phan, id_chuc_vu, id_don_vi } = form.value;

          if (id_bo_phan == null || id_chuc_vu == null || id_don_vi == null) {
            return;
          }

          const profilePayload = {
            ho_va_ten: form.value.ho_va_ten,
            so_dien_thoai: form.value.so_dien_thoai,
            id_bo_phan,
            id_chuc_vu,
            id_don_vi,
            email: form.value.email || null,
          };

          if (isCreating.value) {
            const {
              data: {
                data: { user: createdUser },
              },
            } = await UserService.create({
              nguoiDungInstance: {
                ...profilePayload,
                mat_khau: form.value.mat_khau,
              },
              ...(actorIsAdmin.value &&
                canManage.value && {
                  roles: selectedRoles.value,
                  permissions: buildDirectPermissionsPayload(),
                }),
            });
            await swalNotification("Tạo người dùng thành công", "success");
            await replace(`/quan-ly/nguoi-dung/cap-nhat/${createdUser.id}`);
          } else {
            await UserService.update(id.value, profilePayload);

            // Roles/permissions are saved instantly on checkbox toggle
            // (see handleAuthChange), so profile save only covers profile.

            if (form.value.mat_khau) {
              await UserService.updatePassword(id.value, {
                mat_khau: form.value.mat_khau,
                mat_khau_confirm: form.value.mat_khau_confirm,
              });
            }

            await swalNotification("Cập nhật người dùng thành công", "success");
            await loadUserDetail();
          }
        } catch (error) {
          await swalErrNotification(
            error,
            isCreating.value
              ? "Không thể tạo người dùng, vui lòng thử lại"
              : "Không thể cập nhật người dùng, vui lòng thử lại"
          );
        } finally {
          await endLoading();
        }
      });
    };

    return {
      actorIsAdmin,
      ButtonTypeColors,
      ButtonsType,
      boPhanOptions,
      canManage,
      chucVuOptions,
      clear,
      donViOptionLabel,
      donViOptions,
      form,
      formRef,
      goBack,
      handleAuthChange,
      isCreating,
      isLoading,
      isUnitLevel,
      pageLoading,
      permissionGroups,
      roleOptions,
      rules,
      savingAuth,
      selectedPermissions,
      selectedRoles,
      submit,
    };
  },
});
